<template>
  <div class="height-100">
    <div style="height: 50px; line-height: 50px; border-bottom: 1px solid #4f4f4f; font-weight: 450;">
      <a-row>
        <a-col :span="1"></a-col>
        <a-col :span="22">选择隐患识别（专题）</a-col>
        <a-col :span="1"></a-col>
      </a-row>
    </div>

    <!-- 面包屑导航 -->
    <div class="breadcrumb-wrapper" v-if="breadcrumb.length">
      <a-breadcrumb>
        <a-breadcrumb-item @click="navigateToRoot">
          <span class="bread-link">全部</span>
        </a-breadcrumb-item>
        <a-breadcrumb-item
          v-for="(item, idx) in breadcrumb"
          :key="item.id"
          @click="navigateToLevel(idx)"
        >
          <span class="bread-link">{{ item.name }}</span>
        </a-breadcrumb-item>
      </a-breadcrumb>
    </div>

    <!-- 当前层级列表 -->
    <div class="scrollbar" :style="{ height: scrollHeight + 'px' }">
      <div
        v-for="item in currentItems"
        :key="item.id"
        class="special-item"
        @click="handleItemClick(item)"
        :class="{ 'disabled-item': item.disabled }"
      >
        <div class="item-name">{{ item.name }}</div>
        <div class="item-arrow" v-if="hasChildren(item)">
          <RightOutlined />
        </div>
      </div>
      <a-empty v-if="!currentItems.length" :image-style="{ height: '60px', marginTop: '60px' }" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue'
import { RightOutlined } from '@ant-design/icons-vue'
import { getAllSpecialTree, getAllSpecialData } from '/@/api/insp/insp-regulations'
import { message } from 'ant-design-vue'
import { useMyStore } from '/@/store'

const store = useMyStore()
const scrollHeight = ref(0)

// 数据结构
interface SpecialNode {
  id: number
  name: string
  code: string
  parentId: number
  children?: SpecialNode[]
  type?: string
  category?: string
  isLeaf?: boolean // 新增：标记是否为可选的叶子节点（第三层）
  disabled?: boolean
}

interface ThirdNode {
  id: number
  appName: string
  appCode: string
  type: string
  category: string
}

// 全量树数据（原始，用于查找）
const fullTreeData = ref<SpecialNode[]>([])

// 当前显示的节点列表
const currentItems = ref<SpecialNode[]>([])
// 面包屑：存储从根到当前层级的路径节点（不包含根）
const breadcrumb = ref<SpecialNode[]>([])

// 判断是否有子节点
const hasChildren = (node: SpecialNode) => {
  return node.children && node.children.length > 0
}

// 加载专题数据（复用原有逻辑，添加 isLeaf 标记）
const loadSpecialData = async () => {
  try {
    const treeRes = await getAllSpecialTree()
    const rawTreeData: SpecialNode[] = treeRes.data || []
    const thirdRes = await getAllSpecialData()
    const thirdNodes: ThirdNode[] = thirdRes.data || []

    // 构建 category -> third nodes 映射
    const childrenMap = new Map<string, ThirdNode[]>()
    thirdNodes.forEach(node => {
      const cat = node.category
      if (!childrenMap.has(cat)) childrenMap.set(cat, [])
      childrenMap.get(cat)!.push(node)
    })

    // 将第三层挂载到第二层，并标记 isLeaf
    const processed = rawTreeData.map(root => {
      // 第一层节点：不可选
      const rootNode: SpecialNode = { ...root, isLeaf: false }
      if (root.children && root.children.length) {
        rootNode.children = root.children.map(second => {
          // 第二层节点：不可选
          const secondNode: SpecialNode = { ...second, isLeaf: false }
          const matched = childrenMap.get(second.code) || []
          const thirdLevelChildren = matched.map(third => ({
            id: third.appCode,
            name: third.appName,
            code: third.appCode,
            parentId: second.id,
            type: third.type,
            category: third.category,
            children: [],
            isLeaf: true, // 第三层节点为可选叶子
            disabled: !third.appCode,
          } as SpecialNode))
          secondNode.children = thirdLevelChildren
          return secondNode
        })
      }
      return rootNode
    })

    fullTreeData.value = processed
    // 初始显示第一层（根节点）
    currentItems.value = processed
    breadcrumb.value = []
  } catch (error) {
    console.error('加载专题树失败', error)
    message.error('加载专题树失败')
  }
}

// 点击某一项
const handleItemClick = (item: SpecialNode) => {
  if (item.isLeaf) {
    if (item.disabled) {
      message.warning('该专题不可选（缺少应用编码）')
      return
    }
    selectSpecial(item)
    return
  }
  // 如果是可选叶子节点（第三层），直接选择
  if (item.isLeaf) {
    selectSpecial(item)
    return
  }

  // 如果有子节点，进入下一级
  if (hasChildren(item)) {
    breadcrumb.value.push(item)
    currentItems.value = item.children!
    return
  }

  // 既不是叶子节点又没有子节点（理论上第二层无数据时会出现）
  message.warning('该分类下暂无可用专题，请选择其他分类')
}

// 选择专题（叶子节点）
const selectSpecial = (node: SpecialNode) => {
  const selected = {
    id: node.code,
    portion: node.name,
    portion_code: node.code,
    portion_type: node.type || '',
  }
  store.commit('SET_SELECTED_SPECIAL_INFO', selected)
  // 触发关闭面板事件
  window.dispatchEvent(new CustomEvent('closeSpecialPanel'))
}

// 面包屑：回到根
const navigateToRoot = () => {
  currentItems.value = fullTreeData.value
  breadcrumb.value = []
}

// 面包屑：回到指定层级（idx 是 breadcrumb 中的索引）
const navigateToLevel = (idx: number) => {
  // 保留目标层级之前的路径
  const targetPath = breadcrumb.value.slice(0, idx + 1)
  breadcrumb.value = targetPath

  // 根据路径获取目标节点列表
  let items = fullTreeData.value
  for (const node of targetPath) {
    const found = items.find(i => i.id === node.id)
    if (found && found.children) {
      items = found.children
    } else {
      items = []
      break
    }
  }
  currentItems.value = items
}

onMounted(() => {
  loadSpecialData()
  // 计算滚动区域高度（减去头部和面包屑高度）
  const headerHeight = 50
  const breadcrumbHeight = 40
  const totalHeight = document.body.clientHeight
  scrollHeight.value = totalHeight - headerHeight - breadcrumbHeight - 20 // 留一些边距
})
</script>

<style lang="scss" scoped>
/* 样式保持不变 */
.height-100 {
  height: 100%;
}

.breadcrumb-wrapper {
  height: 40px;
  line-height: 40px;
  padding: 10px;
  border-bottom: 1px solid #3a3a3a;
  background-color: #2a2a2a;

  .bread-link {
    cursor: pointer;
    color: #a0a0a0;

    &:hover {
      color: #1890ff;
    }
  }
}

.scrollbar {
  overflow-y: auto;
  padding: 10px 0;
}

.special-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  margin: 4px 12px;
  background-color: #3c3c3c;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #4a4a4a;
  }

  .item-name {
    color: #fff;
    font-size: 14px;
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .item-arrow {
    color: #aaa;
    font-size: 12px;
    margin-left: 12px;
  }
}

.disabled-item {
  opacity: 0.5;
  cursor: not-allowed;
  &:hover {
    background-color: #3c3c3c; // 保持与普通项 hover 不同，表示禁用
  }
}
</style>
